import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { get_user_all_notifications, notification_status_change } from '../../../../app/modules/auth/core/_requests';
import { KTIcon } from '../../../helpers';
import clsx from 'clsx';
import { TimeAgo } from '../../../../app/helper-functions/TimeAgo';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Type for the potentially parsed notification_description
type ParsedDescriptionObject = {
    description: string;
    requisition_id?: string | number;
    quotation_id?: string | number;
    stockout_id?: string | number;
    stockin_id?: string | number;
};
type NotificationDescription = ParsedDescriptionObject | string;

// Type for a single notification item (after client-side parsing of description)
type NotificationItem = {
    id: number;
    notification_title?: string;
    notification_description: NotificationDescription;
    notification_type?: string;
    is_read: number;
    updated_by?: string | null;
    created_at: string;
    updated_at: string;
};

// Type for a raw notification object as it comes from the API
type RawNotificationFromAPI = Omit<NotificationItem, 'notification_description' | 'updated_by'> & {
    notification_description: string;
    updated_by: string | null;
};

// Type for the pagination metadata from the API
type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

// Type for the 'data' object within the API response
type ApiDataPayload = {
    notifications: RawNotificationFromAPI[];
    pagination: ApiPaginationInfo;
    links: {
        first_page_url: string | null;
        last_page_url: string | null;
        prev_page_url: string | null;
        next_page_url: string | null;
    };
};

// Type for the overall successful API response structure
type ApiFullResponse = {
    data: { // This outer 'data' is typically from Axios or a similar HTTP client
        success: boolean;
        message: string;
        data: ApiDataPayload; // This inner 'data' is from your API structure
    };
};


function AllNotifications() {
    const [allNotificationsList, setAllNotificationsList] = useState<NotificationItem[]>([]);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Pagination state
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);

    // State to store 'from' and 'to' for display purposes
    const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
    const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);


    const {
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery<ApiFullResponse, Error>(
        ['get_user_all_notifications', currentPage, itemsPerPage],
        () => get_user_all_notifications(currentPage, itemsPerPage),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onSuccess: (response) => {
                // Actual API payload is inside response.data.data
                const apiPayload = response.data.data;

                if (apiPayload && apiPayload.notifications && Array.isArray(apiPayload.notifications)) {
                    const parsedNotifications: NotificationItem[] = apiPayload.notifications.map((rawNotification: RawNotificationFromAPI) => {
                        let parsedDesc: NotificationDescription = rawNotification.notification_description;
                        try {
                            if (typeof rawNotification.notification_description === 'string' &&
                                (rawNotification.notification_description.startsWith('{') || rawNotification.notification_description.startsWith('['))) {
                                parsedDesc = JSON.parse(rawNotification.notification_description);
                            }
                        } catch (err) {
                            console.warn(`Failed to parse description for notification ID ${rawNotification.id}:`, rawNotification.notification_description, err);
                        }
                        return {
                            id: rawNotification.id,
                            notification_title: rawNotification.notification_title,
                            notification_description: parsedDesc,
                            notification_type: rawNotification.notification_type,
                            is_read: rawNotification.is_read,
                            updated_by: rawNotification.updated_by,
                            created_at: rawNotification.created_at,
                            updated_at: rawNotification.updated_at,
                        };
                    });

                    setAllNotificationsList(parsedNotifications);

                    if (apiPayload.pagination) {
                        setTotalPages(apiPayload.pagination.last_page);
                        setTotalItems(apiPayload.pagination.total);
                        setCurrentRangeFrom(apiPayload.pagination.from); // Store 'from' from API
                        setCurrentRangeTo(apiPayload.pagination.to);     // Store 'to' from API
                    } else {
                        // Fallback if pagination object is somehow missing
                        setTotalPages(1);
                        setTotalItems(parsedNotifications.length);
                        setCurrentRangeFrom(parsedNotifications.length > 0 ? 1 : 0);
                        setCurrentRangeTo(parsedNotifications.length);
                    }
                } else {
                    console.warn("Unexpected API response structure or no notifications array:", apiPayload);
                    setAllNotificationsList([]);
                    setTotalPages(0);
                    setTotalItems(0);
                    setCurrentRangeFrom(1); // Reset
                    setCurrentRangeTo(0);   // Reset
                }
            },
            onError: (err) => {
                toast.error(`Error fetching notifications: ${err.message}`, {
                    position: 'top-right', autoClose: 3000,
                });
                setAllNotificationsList([]);
                setTotalPages(0);
                setTotalItems(0);
                setCurrentRangeFrom(1); // Reset
                setCurrentRangeTo(0);   // Reset
            }
        }
    );

    const handleStatusChange = useMutation(
        (id: number) => notification_status_change(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['get_user_all_notifications', currentPage, itemsPerPage]);
                toast.success("Notification marked as read.", { autoClose: 1500 });
            },
            onError: (error: any) => {
                const errorMessage = error.response?.data?.message || "Failed to update notification status.";
                toast.error(errorMessage, { position: 'top-right', autoClose: 2000 });
            }
        }
    );

    const getDescriptionForDisplay = (description: NotificationDescription): string => {
        if (typeof description === 'string') {
            try {
                if (description.startsWith('{')) {
                    const parsed = JSON.parse(description) as ParsedDescriptionObject;
                    return parsed.description || description;
                }
                return description;
            } catch (e) { return description; }
        } else if (typeof description === 'object' && description !== null && 'description' in description) {
            return (description as ParsedDescriptionObject).description;
        }
        return "No description available.";
    };

    const getParsedDescObject = (description: NotificationDescription): ParsedDescriptionObject | null => {
        if (typeof description === 'string') {
            try {
                if (description.startsWith('{')) {
                    return JSON.parse(description) as ParsedDescriptionObject;
                }
                return null;
            } catch (e) {
                console.error("Failed to parse string description for navigation:", description, e);
                return null;
            }
        }
        return description as ParsedDescriptionObject;
    };

     const handleViewRequisition = (description: NotificationDescription) => {
        const parsedObject = getParsedDescObject(description);
        if (parsedObject && parsedObject.requisition_id) {
            navigate("/requisitions/view-requisition", { state: { id: parsedObject.requisition_id } });
        } else { toast.error("Could not retrieve requisition details or ID missing."); }
    };
    const handleViewQuotation = (description: NotificationDescription) => {
        const parsedObject = getParsedDescObject(description);
        if (parsedObject && parsedObject.quotation_id) {
            navigate("/quotations/view-quotation", { state: { id: parsedObject.quotation_id } });
        } else { toast.error("Could not retrieve quotation details or ID missing."); }
    };
    const handleViewStockOut = (description: NotificationDescription) => {
        const parsedObject = getParsedDescObject(description);
        if (parsedObject && parsedObject.stockout_id) {
            navigate("/stocks/view-stock-out", { state: { id: parsedObject.stockout_id } });
        } else { toast.error("Could not retrieve stock out details or ID missing."); }
    };
    const handleViewStockIn = (description: NotificationDescription) => {
        const parsedObject = getParsedDescObject(description);
        if (parsedObject && (parsedObject.stockin_id || parsedObject.stockout_id)) {
            const idToUse = parsedObject.stockin_id || parsedObject.stockout_id;
            navigate("/stocks/view-stock-in", { state: { id: idToUse } });
        } else { toast.error("Could not retrieve stock in details or ID missing."); }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages && newPage !== currentPage) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newItemsPerPage = Number(event.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    if (isLoading && !isFetching && currentPage === 1) {
        return <div className='px-8 py-5 text-center'>Loading notifications...</div>;
    }

    if (isError && !isFetching ) {
        return <div className='px-8 py-5 text-center text-danger'>Error loading notifications: {error?.message}</div>;
    }

    return (
        <>
            <div className='d-flex justify-content-between align-items-center px-8 pt-5'>
                <h3>Notifications</h3>
                {totalItems > 0 && (
                    <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Items per page:</span>
                        <select
                            className='form-select form-select-sm w-auto'
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            disabled={isFetching}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                )}
            </div>

            {isFetching && <div className='px-8 text-muted fst-italic'>Updating notifications...</div>}

            <div className='scroll-y mh-325px my-5 px-8'>
                {!isFetching && allNotificationsList.length === 0 ? (
                    <div className="text-center mt-4 py-5">
                        <p>No notifications to display.</p>
                    </div>
                ) : (
                    allNotificationsList.map((notification) => ( // index is not strictly needed if id is unique
                        <div key={`notification-${notification.id}`} className="mb-3"> {/* Use notification.id if unique */}
                            {notification.is_read === 1 ? (
                                <div className='d-flex flex-stack py-4 border-bottom rounded'>
                                    <div className='d-flex align-items-center'>
                                        <div className='symbol symbol-35px me-4'>
                                            {notification.notification_type === 'Requisition' && (<span className={clsx('symbol-label', `bg-light-primary`)}><KTIcon iconName="abstract-25" className={`fs-2 text-primary`} /></span>)}
                                            {notification.notification_type === 'Quotation' && (<span className={clsx('symbol-label', `bg-light-warning`)}><KTIcon iconName="abstract-28" className={`fs-2 text-warning`} /></span>)}
                                            {(notification.notification_type === 'Stockout' || notification.notification_type === 'Stockin') && (<span className={clsx('symbol-label', `bg-light-danger`)}><KTIcon iconName="package" className={`fs-2 text-danger`} /></span>)}
                                            {!['Requisition', 'Quotation', 'Stockout', 'Stockin'].includes(notification.notification_type || '') && (<span className={clsx('symbol-label', `bg-light-secondary`)}><KTIcon iconName="notification-on" className={`fs-2 text-secondary`} /></span>)}
                                        </div>
                                        <div className='mb-0 me-2'>
                                            <span className='fs-6 text-gray-800 text-hover-primary fw-bolder cursor-pointer'
                                                onClick={() => {
                                                    if (notification.notification_type === 'Requisition') handleViewRequisition(notification.notification_description);
                                                    else if (notification.notification_type === 'Quotation') handleViewQuotation(notification.notification_description);
                                                    else if (notification.notification_type === 'Stockout') handleViewStockOut(notification.notification_description);
                                                    else if (notification.notification_type === 'Stockin') handleViewStockIn(notification.notification_description);
                                                }}>
                                                {notification.notification_title || 'Notification'}
                                            </span>
                                            <div className='text-gray-500 fs-7'>{getDescriptionForDisplay(notification.notification_description)}</div>
                                        </div>
                                    </div>
                                    <span className='badge badge-light fs-8'>{TimeAgo(notification.created_at)}</span>
                                </div>
                            ) : (
                                <div className={clsx('d-flex flex-stack py-3 px-3 border-bottom rounded', {
                                    'bg-light-primary border-primary border-2 border-start': notification.notification_type === 'Requisition',
                                    'bg-light-warning border-warning border-2 border-start': notification.notification_type === 'Quotation',
                                    'bg-light-info border-info border-2 border-start': notification.notification_type === 'Stockout' || notification.notification_type === 'Stockin',
                                    'bg-light-secondary border-secondary border-2 border-start': !['Requisition', 'Quotation', 'Stockout', 'Stockin'].includes(notification.notification_type || '')
                                })}>
                                     <div className='d-flex align-items-center'>
                                        <div className='symbol symbol-35px me-4'>
                                            {notification.notification_type === 'Requisition' && (<span className={clsx('symbol-label', `bg-light-primary`)}><KTIcon iconName="abstract-25" className={`fs-2 fw-bolder text-primary`} /></span>)}
                                            {notification.notification_type === 'Quotation' && (<span className={clsx('symbol-label', `bg-light-warning`)}><KTIcon iconName="abstract-28" className={`fs-2 fw-bolder text-warning`} /></span>)}
                                            {(notification.notification_type === 'Stockout' || notification.notification_type === 'Stockin') && (<span className={clsx('symbol-label', `bg-light-info`)}><KTIcon iconName="package" className={`fs-2 fw-bolder text-info`} /></span>)}
                                            {!['Requisition', 'Quotation', 'Stockout', 'Stockin'].includes(notification.notification_type || '') && (<span className={clsx('symbol-label', `bg-light-dark`)}><KTIcon iconName="notification-on" className={`fs-2 fw-bolder text-dark`} /></span>)}
                                        </div>
                                        <div className='mb-0 me-2'>
                                            <span className='fs-6 text-gray-800 text-hover-primary fw-bolder cursor-pointer'
                                                onClick={() => {
                                                    if (notification.notification_type === 'Requisition') handleViewRequisition(notification.notification_description);
                                                    else if (notification.notification_type === 'Quotation') handleViewQuotation(notification.notification_description);
                                                    else if (notification.notification_type === 'Stockout') handleViewStockOut(notification.notification_description);
                                                    else if (notification.notification_type === 'Stockin') handleViewStockIn(notification.notification_description);
                                                    if (notification && notification.id) { handleStatusChange.mutate(notification.id); }
                                                }}>
                                                {notification.notification_title || 'Notification'}
                                            </span>
                                            <div className='text-gray-600 fs-7'> {getDescriptionForDisplay(notification.notification_description)} </div>
                                        </div>
                                    </div>
                                    <span className={clsx('badge fs-8', {
                                        'badge-primary': notification.notification_type === 'Requisition',
                                        'badge-warning': notification.notification_type === 'Quotation',
                                        'badge-info': notification.notification_type === 'Stockout' || notification.notification_type === 'Stockin',
                                        'badge-dark': !['Requisition', 'Quotation', 'Stockout', 'Stockin'].includes(notification.notification_type || '')
                                    })}>{TimeAgo(notification.created_at)}</span>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && allNotificationsList.length > 0 && (
                <div className='d-flex justify-content-between align-items-center px-8 pb-5 pt-3'>
                    <div className='fs-6 fw-semibold text-gray-700'>
                        Showing {currentRangeFrom} to {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} results
                    </div>
                    <nav>
                        <ul className="pagination pagination-sm">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isFetching} aria-label="Previous">
                                    <span aria-hidden="true">«</span>
                                </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(pageNumber => pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2))
                                .map((pageNumber, index, arr) => {
                                    const isEllipsisNeeded = index > 0 && arr[index - 1] + 1 !== pageNumber;
                                    return (
                                        <React.Fragment key={pageNumber}>
                                            {isEllipsisNeeded && (<li className="page-item disabled"><span className="page-link">...</span></li>)}
                                            <li className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(pageNumber)} disabled={isFetching}>{pageNumber}</button>
                                            </li>
                                        </React.Fragment>
                                    );
                                })}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || isFetching} aria-label="Next">
                                    <span aria-hidden="true">»</span>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </>
    );
}

export default AllNotifications;